import React, { useState, useContext, useEffect } from "react";
import MainContainer from "../../components/MainContainer";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import ForbiddenPage from "../../components/ForbiddenPage";
import useCompanies from "../../hooks/useCompanies";
import usePlans from "../../hooks/usePlans";
import { toast } from "react-toastify";
import moment from "moment";
import { useDate } from "../../hooks/useDate";
import ConfirmationModal from "../../components/ConfirmationModal";
import ButtonWithSpinner from "../../components/ButtonWithSpinner";
import { Edit as EditIcon, Delete as DeleteIcon, Save as SaveIcon, Close as CloseIcon, Add as AddIcon } from "@material-ui/icons";
import Modal from "@material-ui/core/Modal";
import Backdrop from "@material-ui/core/Backdrop";
import { useTheme } from "@material-ui/core/styles";

const useStyles = () => ({
  container: {
    padding: "24px",
    background: "#f5f7fa",
    minHeight: "100vh",
  },
  header: {
    marginBottom: "24px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "16px",
  },
  title: {
    fontSize: "28px",
    fontWeight: 700,
    color: "#1a1a1a",
    margin: 0,
  },
  formCard: {
    background: "white",
    borderRadius: "12px",
    padding: "24px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    border: "1px solid #e8ecf1",
    marginBottom: "24px",
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "16px",
    marginBottom: "16px",
  },
  formGroup: {
    display: "flex",
    flexDirection: "column",
  },
  label: {
    fontSize: "12px",
    fontWeight: 600,
    color: "#666",
    marginBottom: "6px",
    display: "block",
  },
  input: {
    padding: "10px 12px",
    border: "1px solid #e8ecf1",
    borderRadius: "6px",
    fontSize: "13px",
    fontFamily: "inherit",
    background: "#fafbfc",
    transition: "all 0.2s ease",
    "&:focus": {
      outline: "none",
      borderColor: "#3b82f6",
      background: "white",
      boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)",
    },
  },
  select: {
    padding: "10px 12px",
    border: "1px solid #e8ecf1",
    borderRadius: "6px",
    fontSize: "13px",
    fontFamily: "inherit",
    background: "#fafbfc",
    cursor: "pointer",
    transition: "all 0.2s ease",
    "&:focus": {
      outline: "none",
      borderColor: "#3b82f6",
      background: "white",
      boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)",
    },
  },
  formActions: {
    display: "flex",
    gap: "12px",
    justifyContent: "flex-end",
    paddingTop: "16px",
    borderTop: "1px solid #e8ecf1",
    flexWrap: "wrap",
  },
  button: {
    padding: "10px 20px",
    background: "#3b82f6",
    color: "white",
    border: "none",
    borderRadius: "6px",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.3s ease",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    "&:hover": {
      background: "#2563eb",
      transform: "translateY(-1px)",
      boxShadow: "0 2px 8px rgba(59, 130, 246, 0.3)",
    },
    "&:disabled": {
      background: "#cbd5e1",
      cursor: "not-allowed",
      transform: "none",
    },
  },
  buttonSecondary: {
    padding: "10px 20px",
    background: "#e8ecf1",
    color: "#1a1a1a",
    border: "none",
    borderRadius: "6px",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.3s ease",
    "&:hover": {
      background: "#d1d5db",
    },
  },
  buttonDanger: {
    padding: "10px 20px",
    background: "#ef4444",
    color: "white",
    border: "none",
    borderRadius: "6px",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.3s ease",
    "&:hover": {
      background: "#dc2626",
    },
    "&:disabled": {
      background: "#cbd5e1",
      cursor: "not-allowed",
    },
  },
  tableCard: {
    background: "white",
    borderRadius: "12px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    border: "1px solid #e8ecf1",
    overflow: "visible",
  },
  tableContainer: {
    overflowX: "auto",
    borderRadius: "12px 12px 0 0",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  tableHeader: {
    background: "#f9fafb",
    borderBottom: "2px solid #e8ecf1",
    position: "sticky",
    top: 0,
  },
  tableHeaderCell: {
    padding: "12px",
    textAlign: "left",
    fontSize: "12px",
    fontWeight: 700,
    color: "#666",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  tableRow: {
    borderBottom: "1px solid #e8ecf1",
    transition: "all 0.2s ease",
    "&:hover": {
      background: "#f9fafb",
    },
  },
  tableCell: {
    padding: "12px",
    fontSize: "13px",
    color: "#1a1a1a",
  },
  tableActions: {
    display: "flex",
    gap: "8px",
  },
  iconButton: {
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: "6px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "4px",
    transition: "all 0.2s ease",
    color: "#3b82f6",
    "&:hover": {
      background: "rgba(59, 130, 246, 0.1)",
    },
  },
  statusBadge: {
    display: "inline-block",
    padding: "4px 8px",
    borderRadius: "4px",
    fontSize: "12px",
    fontWeight: 600,
  },
  statusActive: {
    background: "#d1fae5",
    color: "#065f46",
  },
  statusInactive: {
    background: "#fee2e2",
    color: "#991b1b",
  },
  warningRow: {
    background: "#fffbeb",
  },
  dangerRow: {
    background: "#fecaca",
  },
  emptyState: {
    textAlign: "center",
    padding: "40px 20px",
    color: "#999",
  },
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  modalContent: {
    background: "white",
    borderRadius: "12px",
    padding: "32px",
    boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
    maxWidth: "600px",
    width: "90%",
    maxHeight: "90vh",
    overflowY: "auto",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "24px",
    paddingBottom: "16px",
    borderBottom: "1px solid #e8ecf1",
  },
  modalTitle: {
    fontSize: "20px",
    fontWeight: 700,
    color: "#1a1a1a",
    margin: 0,
  },
  closeButton: {
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#999",
    transition: "all 0.2s ease",
    "&:hover": {
      color: "#1a1a1a",
    },
  },
  paginationContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px 20px",
    borderTop: "1px solid #e8ecf1",
    background: "#f9fafb",
    borderBottomLeftRadius: "12px",
    borderBottomRightRadius: "12px",
  },
  paginationInfo: {
    fontSize: "13px",
    color: "#666",
  },
  paginationButtons: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
  },
  paginationButton: {
    padding: "8px 12px",
    background: "#e8ecf1",
    color: "#1a1a1a",
    border: "none",
    borderRadius: "6px",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.2s ease",
    "&:hover": {
      background: "#d1d5db",
    },
    "&:disabled": {
      background: "#f3f4f6",
      color: "#cbd5e1",
      cursor: "not-allowed",
    },
  },
  paginationButtonActive: {
    background: "#3b82f6",
    color: "white",
    "&:hover": {
      background: "#2563eb",
    },
  },
  filterContainer: {
    background: "white",
    borderRadius: "12px",
    padding: "20px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    border: "1px solid #e8ecf1",
    marginBottom: "24px",
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
    gap: "16px",
    alignItems: "flex-end",
  },
  filterGroup: {
    display: "flex",
    flexDirection: "column",
  },
  addButton: {
    padding: "10px 20px",
    background: "#10b981",
    color: "white",
    border: "none",
    borderRadius: "6px",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.3s ease",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    "&:hover": {
      background: "#059669",
      transform: "translateY(-1px)",
      boxShadow: "0 2px 8px rgba(16, 185, 129, 0.3)",
    },
  },
});

export default function Companies() {
  const styles = useStyles();
  const theme = useTheme();
  const { user } = useContext(AuthContext);
  const { list, save, update, remove } = useCompanies();
  const { list: listPlans } = usePlans();
  const { dateToClient, datetimeToClient } = useDate();

  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [plans, setPlans] = useState([]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    planId: "",
    status: true,
    dueDate: "",
    recurrence: "",
    password: "",
    document: "",
    generateInvoice: true,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [companiesList, plansList] = await Promise.all([list(), listPlans()]);
      setRecords(companiesList);
      setPlans(plansList);
      setCurrentPage(1);
    } catch (e) {
      toast.error("Erro ao carregar dados");
    }
    setLoading(false);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    let finalValue = type === "checkbox" ? checked : value;
    
    // Aplicar máscara de CPF/CNPJ no campo documento
    if (name === "document") {
      const numbers = value.replace(/\D/g, '');
      
      if (numbers.length <= 11) {
        // Máscara de CPF: 000.000.000-00
        finalValue = numbers
          .replace(/(\d{3})(\d)/, '$1.$2')
          .replace(/(\d{3})(\d)/, '$1.$2')
          .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
      } else {
        // Máscara de CNPJ: 00.000.000/0000-00
        finalValue = numbers
          .slice(0, 14)
          .replace(/(\d{2})(\d)/, '$1.$2')
          .replace(/(\d{3})(\d)/, '$1.$2')
          .replace(/(\d{3})(\d)/, '$1/$2')
          .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
      }
    }
    
    setFormData((prev) => {
      const newData = {
        ...prev,
        [name]: finalValue,
      };
      
      // Se a data de vencimento for removida (ilimitada), desabilita geração de fatura
      if (name === "dueDate" && !value) {
        newData.generateInvoice = false;
      }
      
      return newData;
    });
  };

  const handleSelectRecord = (record) => {
    // Impedir seleção de empresa com super admin
    if (record?.Users?.some(u => u.super === true)) {
      toast.error("Super admin não pode ser deletado");
      return;
    }
    
    setFormData({
      id: record.id,
      name: record.name || "",
      email: record.email || "",
      phone: record.phone || "",
      planId: record.planId || "",
      status: record.status !== false,
      dueDate: record.dueDate ? moment(record.dueDate).format("YYYY-MM-DD") : "",
      recurrence: record.recurrence || "",
      password: "",
      document: record.document || "",
      generateInvoice: record.generateInvoice !== false,
    });
    setSelectedRecord(record);
    setShowModal(true);
  };

  const handleClear = () => {
    setFormData({
      name: "",
      email: "",
      phone: "",
      planId: "",
      status: true,
      dueDate: "",
      recurrence: "",
      password: "",
      document: "",
      generateInvoice: true,
    });
    setSelectedRecord(null);
    setShowModal(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = { ...formData };
      
      // Validar documento
      if (!data.document || data.document.trim() === "") {
        toast.error("Documento (CPF/CNPJ) é obrigatório");
        setLoading(false);
        return;
      }
      
      const cleanDocument = data.document.replace(/\D/g, '');
      if (cleanDocument.length !== 11 && cleanDocument.length !== 14) {
        toast.error("Documento deve ser um CPF (11 dígitos) ou CNPJ (14 dígitos)");
        setLoading(false);
        return;
      }
      
      // Converter status para boolean
      data.status = data.status === true || data.status === "true";
      
      // Converter generateInvoice para boolean
      data.generateInvoice = data.generateInvoice === true || data.generateInvoice === "true";
      
      // Converter planId para número
      if (data.planId) {
        data.planId = parseInt(data.planId);
      }
      
      // Tratar data de vencimento
      if (data.dueDate === "" || !moment(data.dueDate).isValid()) {
        data.dueDate = null;
        data.generateInvoice = false; // Se não tem data, não gera fatura
      }
      
      console.log("Dados sendo enviados:", data);
      
      if (data.id) {
        await update(data);
      } else {
        await save(data);
      }
      await loadData();
      handleClear();
      toast.success("Operação realizada com sucesso!");
    } catch (e) {
      const errorMsg = e?.response?.data?.error || e?.message || "Erro ao salvar empresa";
      toast.error(errorMsg);
      console.error("Erro completo:", e?.response?.data || e);
      console.error("Status:", e?.response?.status);
      console.error("Headers:", e?.response?.headers);
    }
    setLoading(false);
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      // Verificar se é super admin
      const isSuperAdmin = selectedRecord?.Users?.some(u => u.super === true);
      if (isSuperAdmin) {
        toast.error("Não é possível deletar uma empresa com super admin!");
        setLoading(false);
        return;
      }
      
      await remove(selectedRecord.id);
      await loadData();
      handleClear();
      setShowConfirmDialog(false);
      toast.success("Empresa deletada com sucesso!");
    } catch (e) {
      toast.error("Erro ao deletar empresa");
    }
    setLoading(false);
  };

  const getRowStyle = (record) => {
    if (moment(record.dueDate).isValid()) {
      const now = moment();
      const dueDate = moment(record.dueDate);
      const diff = dueDate.diff(now, "days");
      if (diff >= 1 && diff <= 5) {
        return styles.warningRow;
      }
      if (diff <= 0) {
        return styles.dangerRow;
      }
    }
    return {};
  };

  if (!user.super) {
    return <ForbiddenPage />;
  }

  // Cálculo de paginação
  const filteredRecords = records.filter((record) => {
    const matchesSearch = 
      (record.name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (record.email || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (record.phone && record.phone.includes(searchTerm));
    
    const matchesStatus = 
      filterStatus === "" || 
      (filterStatus === "active" && record.status) ||
      (filterStatus === "inactive" && !record.status);
    
    return matchesSearch && matchesStatus;
  });

  const totalPages = Math.ceil(filteredRecords.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedRecords = filteredRecords.slice(startIndex, endIndex);

  return (
    <MainContainer>
      <div style={styles.container}>
          <div style={styles.header}>
          <h1 style={styles.title}>🏢 Empresas</h1>
          <button
            onClick={() => {
              setFormData({
                name: "",
                email: "",
                phone: "",
                planId: "",
                status: true,
                dueDate: "",
                recurrence: "",
                password: "",
                document: "",
                generateInvoice: true,
              });
              setSelectedRecord(null);
              setShowModal(true);
            }}
            style={{
              padding: "10px",
              background: "var(--primaryColor, #065183)",
              color: "white",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              transition: "all 0.3s ease",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onMouseEnter={(e) => {
              e.target.style.opacity = "0.9";
              e.target.style.transform = "translateY(-1px)";
              e.target.style.boxShadow = "0 2px 8px rgba(6, 81, 131, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.target.style.opacity = "1";
              e.target.style.transform = "none";
              e.target.style.boxShadow = "none";
            }}
            title="Adicionar Empresa"
          >
            <AddIcon style={{ fontSize: "20px" }} />
          </button>
        </div>

        {/* Filtros */}
        <div style={styles.filterContainer}>
          <div style={styles.filterGroup}>
            <label style={styles.label}>Buscar</label>
            <input
              style={styles.input}
              type="text"
              placeholder="Nome, email ou telefone..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>
          <div style={styles.filterGroup}>
            <label style={styles.label}>Status</label>
            <select
              style={styles.select}
              value={filterStatus}
              onChange={(e) => {
                setFilterStatus(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">Todos</option>
              <option value="active">Ativo</option>
              <option value="inactive">Inativo</option>
            </select>
          </div>
        </div>

        {/* Tabela */}
        <div style={styles.tableCard}>
          <div style={styles.tableContainer}>
            {paginatedRecords.length === 0 ? (
              <div style={styles.emptyState}>
                <p>{records.length === 0 ? "Nenhuma empresa cadastrada" : "Nenhuma empresa encontrada com os filtros aplicados"}</p>
              </div>
            ) : (
              <table style={styles.table}>
                <thead style={styles.tableHeader}>
                  <tr>
                    <th style={styles.tableHeaderCell}>Nome</th>
                    <th style={styles.tableHeaderCell}>Email</th>
                    <th style={styles.tableHeaderCell}>Telefone</th>
                    <th style={styles.tableHeaderCell}>Plano</th>
                    <th style={styles.tableHeaderCell}>Status</th>
                    <th style={styles.tableHeaderCell}>Vencimento</th>
                    <th style={styles.tableHeaderCell}>Último Acesso</th>
                    <th style={styles.tableHeaderCell}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedRecords.map((record) => (
                    <tr
                      key={record.id}
                      style={{
                        ...styles.tableRow,
                        ...getRowStyle(record),
                      }}
                    >
                      <td style={styles.tableCell}>{record.name}</td>
                      <td style={styles.tableCell}>{record.email}</td>
                      <td style={styles.tableCell}>{record.phone || "-"}</td>
                      <td style={styles.tableCell}>
                        {record.plan?.name || "-"}
                      </td>
                      <td style={styles.tableCell}>
                        <span
                          style={{
                            ...styles.statusBadge,
                            ...(record.status
                              ? styles.statusActive
                              : styles.statusInactive),
                          }}
                        >
                          {record.status ? "Ativo" : "Inativo"}
                        </span>
                      </td>
                      <td style={styles.tableCell}>
                        {record.dueDate
                          ? dateToClient(record.dueDate)
                          : <span style={{
                              ...styles.statusBadge,
                              background: "#dbeafe",
                              color: "#1e40af",
                            }}>
                              ∞ Ilimitada
                            </span>}
                      </td>
                      <td style={styles.tableCell}>
                        {record.lastLogin
                          ? datetimeToClient(record.lastLogin)
                          : "-"}
                      </td>
                      <td style={styles.tableCell}>
                        <div style={styles.tableActions}>
                          <button
                            style={styles.iconButton}
                            onClick={() => handleSelectRecord(record)}
                            title="Editar"
                          >
                            <EditIcon style={{ width: 18, height: 18 }} />
                          </button>
                          <button
                            style={{
                              ...styles.iconButton,
                              color: record?.Users?.some(u => u.profile === "admin") || record.id === 1 ? "#cbd5e1" : "#ef4444",
                              cursor: record?.Users?.some(u => u.profile === "admin") || record.id === 1 ? "not-allowed" : "pointer",
                              opacity: record?.Users?.some(u => u.profile === "admin") || record.id === 1 ? 0.5 : 1,
                            }}
                            onClick={() => {
                              if (record?.Users?.some(u => u.profile === "admin") || record.id === 1) {
                                toast.error("Esta empresa não pode ser deletada");
                                return;
                              }
                              setSelectedRecord(record);
                              setShowConfirmDialog(true);
                            }}
                            title={record?.Users?.some(u => u.profile === "admin") || record.id === 1 ? "Não é possível deletar esta empresa" : "Deletar"}
                            disabled={record?.Users?.some(u => u.profile === "admin") || record.id === 1}
                          >
                            <DeleteIcon style={{ width: 18, height: 18 }} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          {records.length > 0 && (
            <div style={styles.paginationContainer}>
              <div style={styles.paginationInfo}>
                Mostrando {startIndex + 1} a {Math.min(endIndex, filteredRecords.length)} de {filteredRecords.length} empresas
              </div>
              <div style={styles.paginationButtons}>
                <button
                  style={{
                    ...styles.paginationButton,
                    opacity: currentPage === 1 ? 0.5 : 1,
                  }}
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                >
                  ← Anterior
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    style={{
                      ...styles.paginationButton,
                      ...(currentPage === page ? styles.paginationButtonActive : {}),
                    }}
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </button>
                ))}
                <button
                  style={{
                    ...styles.paginationButton,
                    opacity: currentPage === totalPages ? 0.5 : 1,
                  }}
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                >
                  Próxima →
                </button>
              </div>
            </div>
          )}
        </div>

        <ConfirmationModal
          title="Deletar Empresa"
          open={showConfirmDialog}
          onClose={() => setShowConfirmDialog(false)}
          onConfirm={handleDelete}
        >
          Tem certeza que deseja deletar esta empresa? Esta ação não pode ser desfeita.
        </ConfirmationModal>

        {/* Modal de Edição */}
        <Modal
          style={styles.modal}
          open={showModal}
          onClose={handleClear}
          BackdropComponent={Backdrop}
          BackdropProps={{ style: { backgroundColor: "rgba(0, 0, 0, 0.5)" } }}
        >
          <div style={styles.modalContent}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>
                {formData.id ? "Editar Empresa" : "Nova Empresa"}
              </h2>
              <button
                style={styles.closeButton}
                onClick={handleClear}
                type="button"
              >
                <CloseIcon style={{ width: 24, height: 24 }} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div style={styles.formGrid}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    Nome
                    <span style={{ color: "#ef4444", marginLeft: "4px" }}>*</span>
                  </label>
                  <input
                    style={styles.input}
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Nome da empresa"
                    required
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    Email
                    <span style={{ color: "#ef4444", marginLeft: "4px" }}>*</span>
                  </label>
                  <input
                    style={styles.input}
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="email@empresa.com"
                    required
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Telefone</label>
                  <input
                    style={styles.input}
                    type="text"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="(11) 99999-9999"
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    Senha
                    <span style={{ color: "#ef4444", marginLeft: "4px" }}>*</span>
                  </label>
                  <input
                    style={styles.input}
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder={formData.id ? "Deixe em branco para não alterar" : "Digite a senha"}
                    required={!formData.id}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    Plano
                    <span style={{ color: "#ef4444", marginLeft: "4px" }}>*</span>
                  </label>
                  <select
                    style={styles.select}
                    name="planId"
                    value={formData.planId}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Selecione um plano</option>
                    {plans.map((plan) => (
                      <option key={plan.id} value={plan.id}>
                        {plan.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    Documento (CPF/CNPJ)
                    <span style={{ color: "#ef4444", marginLeft: "4px" }}>*</span>
                  </label>
                  <input
                    style={styles.input}
                    type="text"
                    name="document"
                    value={formData.document}
                    onChange={handleInputChange}
                    placeholder="000.000.000-00 ou 00.000.000/0000-00"
                    required
                    maxLength={18}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    Data de Vencimento
                    <span style={{ 
                      fontSize: "11px", 
                      fontWeight: 400, 
                      color: "#3b82f6",
                      marginLeft: "6px"
                    }}>
                      (vazio = ilimitada)
                    </span>
                  </label>
                  <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                    <input
                      style={{ ...styles.input, flex: 1 }}
                      type="date"
                      name="dueDate"
                      value={formData.dueDate}
                      onChange={handleInputChange}
                    />
                    {formData.dueDate && (
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ 
                          ...prev, 
                          dueDate: "",
                          generateInvoice: false 
                        }))}
                        style={{
                          padding: "10px 12px",
                          background: "#f3f4f6",
                          color: "#666",
                          border: "1px solid #e8ecf1",
                          borderRadius: "6px",
                          fontSize: "11px",
                          fontWeight: 600,
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                          whiteSpace: "nowrap",
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = "#e8ecf1";
                          e.target.style.color = "#1a1a1a";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = "#f3f4f6";
                          e.target.style.color = "#666";
                        }}
                        title="Tornar ilimitada (desabilita geração de fatura)"
                      >
                        ∞ Ilimitada
                      </button>
                    )}
                  </div>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Recorrência</label>
                  <select
                    style={styles.select}
                    name="recurrence"
                    value={formData.recurrence}
                    onChange={handleInputChange}
                  >
                    <option value="">Nenhuma</option>
                    <option value="MENSAL">Mensal</option>
                    <option value="BIMESTRAL">Bimestral</option>
                    <option value="TRIMESTRAL">Trimestral</option>
                    <option value="SEMESTRAL">Semestral</option>
                    <option value="ANUAL">Anual</option>
                  </select>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Status</label>
                  <select
                    style={styles.select}
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                  >
                    <option value={true}>Ativo</option>
                    <option value={false}>Inativo</option>
                  </select>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    Gerar Fatura
                    {!formData.dueDate && (
                      <span style={{ 
                        fontSize: "11px", 
                        fontWeight: 400, 
                        color: "#ef4444",
                        marginLeft: "6px"
                      }}>
                        (requer data de vencimento)
                      </span>
                    )}
                  </label>
                  <select
                    style={{
                      ...styles.select,
                      opacity: !formData.dueDate ? 0.6 : 1,
                      cursor: !formData.dueDate ? "not-allowed" : "pointer",
                    }}
                    name="generateInvoice"
                    value={formData.generateInvoice}
                    onChange={handleInputChange}
                    disabled={!formData.dueDate}
                  >
                    <option value={true}>Sim</option>
                    <option value={false}>Não</option>
                  </select>
                </div>
              </div>

              <div style={styles.formActions}>
                <button
                  type="button"
                  style={styles.buttonSecondary}
                  onClick={handleClear}
                >
                  <CloseIcon style={{ width: 16, height: 16 }} />
                  Cancelar
                </button>
                {formData.id && (
                  <button
                    type="button"
                    style={styles.buttonDanger}
                    onClick={() => {
                      setShowConfirmDialog(true);
                    }}
                    disabled={formData.id === 1 || selectedRecord?.Users?.some(u => u.super === true)}
                    title={selectedRecord?.Users?.some(u => u.super === true) ? "Não é possível deletar empresa com super admin" : ""}
                  >
                    <DeleteIcon style={{ width: 16, height: 16 }} />
                    Deletar
                  </button>
                )}
                <button
                  type="submit"
                  style={styles.button}
                  disabled={loading}
                >
                  <SaveIcon style={{ width: 16, height: 16 }} />
                  {loading ? "Salvando..." : "Salvar"}
                </button>
              </div>
            </form>
          </div>
        </Modal>
      </div>
    </MainContainer>
  );
}
